import { Checkbox } from '@headlessui/react';
import { useState, useCallback, useEffect } from 'react';
import { AutoCompleteInput } from 'src/components/AutoCompleteInput';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Button } from 'src/components/Button';
import { Label } from 'src/components/Label';
import { Modal } from 'src/components/Modal';
import { SelectBox } from 'src/components/SelectBox';
import { Tab } from 'src/components/Tab';
import { useNotification } from 'src/contexts/NotificationContext';
import { Agent } from 'src/interfaces/agent.interface';
import { Number } from 'src/interfaces/number.interface';
import { processPhoneNumber } from 'src/utils/number';
import { CallContextForm } from './CallContextForm';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { Link } from 'react-router-dom';

interface TestAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent: (Agent & { changed: boolean }) | null;
  callContext?: Record<string, string>;
  setCallContext?: (callContext: Record<string, string>) => void;
  disableCallContext?: boolean;
}

const vmDetectionOptions = [
  { label: 'Off', value: 'off' },
  { label: 'Fastest', value: 'fastest' },
  { label: 'Most Accurate', value: 'most accurate' },
];

export const TestAgentModal = ({
  isOpen,
  onClose,
  agent,
  callContext = {},
  setCallContext = () => {},
  disableCallContext = false,
}: TestAgentModalProps) => {
  const { get, post, remove } = useAuthedRequest();
  const notification = useNotification();

  const [loading, setLoading] = useState(false);
  const [callerNumber, setCallerNumber] = useState('');
  const [receiverNumber, setReceiverNumber] = useState('');

  const [vmDetection, setVmDetection] = useState<
    'off' | 'fastest' | 'most accurate'
  >('off');

  const [doNotCallDetection, setDoNotCallDetection] = useState(false);
  const [hipaaCompliant, setHipaaCompliant] = useState(false);

  const fetchLastNumbers = async (search: string) => {
    try {
      const data = await get(`/numbers/recent?number=${search}`);
      return data.map((i: { id: string; number: string }) => ({
        id: i.id,
        label: i.number,
        value: i.number,
      }));
    } catch (error) {
      console.error('Failed to fetch last numbers:', error);
      return [];
    }
  };

  const searchCallerNumbers = useCallback(async (search: string) => {
    const queryObject: QueryObject = {
      page: '1',
      size: '50',
      filters: JSON.stringify({
        number: search,
      }),
    };

    const query = new URLSearchParams(queryObject as Record<string, string>);

    const data = await get(`/numbers?${query.toString()}`);

    return data.items.map((item: Number) => ({
      value: item.number,
      label: processPhoneNumber(item.number),
    }));
  }, []);

  const makeCall = async () => {
    if (!agent) {
      notification.error('No agent selected');
      return;
    }

    const data = {
      from_number: callerNumber,
      to_number: receiverNumber,
      agent: agent?.changed ? agent : (agent?.id as string),
      on_no_human_answer: vmDetection === 'off' ? 'continue' : 'hangup',
      run_do_not_call_detection: doNotCallDetection,
      hipaa_compliant: hipaaCompliant,
      context: {
        ...callContext,
      },
      telephony_params:
        vmDetection === 'most accurate'
          ? {
              machineDetection: 'DetectMessageEnd',
            }
          : {},
    };

    try {
      setLoading(true);

      await post('/calls', data);

      notification.success('Call has been made successfully');
      onClose();
      setLoading(false);
    } catch (error) {
      const err = error as { status?: number; data?: { message?: string } };
      const errorMessage = err.data?.message;

      if (
        err?.status === 429 &&
        errorMessage &&
        errorMessage.includes('Concurrent call limit')
      ) {
        notification.warning(
          <p>
            {errorMessage}. You can adjust by going to{' '}
            <Link to="/settings">
              <span className="text-neutral-0 font-bold underline">
                settings
              </span>
            </Link>{' '}
            or upgrade your{' '}
            <Link to="/billing">
              <span className="text-neutral-0 font-bold underline">plan</span>
            </Link>{' '}
            to increase your limit .
          </p>,
        );
      } else if (
        err?.status === 429 &&
        errorMessage &&
        !errorMessage.includes('Concurrent call limit')
      ) {
        notification.warning(errorMessage);
      } else if (errorMessage) {
        notification.error(errorMessage);
      } else {
        notification.error(
          'Failed to start call: ' +
            (error instanceof Error ? error.message : 'Unknown error'),
        );
      }
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCallerNumber('');
    setReceiverNumber('');
    onClose();
  };

  useEffect(() => {
    setCallerNumber('');
  }, []);

  return (
    <>
      <Modal
        title={`Test Agent - ${agent?.name}`}
        isOpen={isOpen}
        onClose={handleClose}
        actionButton={
          <>
            <Button
              disabled={loading || !callerNumber || !receiverNumber}
              onClick={makeCall}
            >
              Make Call
            </Button>
          </>
        }
      >
        <Tab
          items={[
            {
              label: 'Call Details',
              content: (
                <>
                  <div className="flex justify-end italic text-sm mt-4">
                    * Required fields
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="mb-6">
                      <Label required htmlFor="provider">
                        Caller Number
                      </Label>

                      <AutoCompleteSelectBox
                        defaultValue={
                          callerNumber
                            ? {
                                value: callerNumber,
                                label: processPhoneNumber(callerNumber),
                              }
                            : undefined
                        }
                        fetchOptions={searchCallerNumbers}
                        onChange={(value) => setCallerNumber(value)}
                        variant="outlined"
                        color="primary"
                        size="medium"
                        disabled={loading}
                      />
                    </div>

                    <div className="mb-6">
                      <AutoCompleteInput
                        label="Receiver Number"
                        id="receiver-number"
                        placeholder="Enter receiver number"
                        value={receiverNumber}
                        onChange={(value) => setReceiverNumber(value)}
                        onRemove={(value) => remove(`/numbers/recent/${value}`)}
                        disabled={loading}
                        required
                        fetchOptions={fetchLastNumbers}
                      />
                    </div>

                    <div className="mb-6">
                      <Label>VM Detection</Label>

                      <SelectBox
                        options={vmDetectionOptions}
                        defaultValue={vmDetectionOptions.find(
                          (o) => o.value === vmDetection,
                        )}
                        onChange={(value) =>
                          setVmDetection(
                            value as 'off' | 'fastest' | 'most accurate',
                          )
                        }
                      />
                    </div>

                    <div className="flex flex-col md:flex-row gap-3">
                      <div className="mb-3 md:mb-0 w-full md:w-1/2 flex items-center gap-2">
                        <Checkbox
                          checked={doNotCallDetection}
                          onChange={(newChecked) =>
                            setDoNotCallDetection(newChecked)
                          }
                          className={`
                        ${doNotCallDetection ? 'bg-primary border-transparent' : 'bg-neutral-0 border-neutral-200'}
                        relative inline-flex h-5 w-5 items-center justify-center rounded border-2
                        transition-colors duration-200 ease-in-out
                        focus:outline-none
                      `}
                        >
                          <span
                            className={`
                          pointer-events-none absolute h-3 w-3 transform
                          ${doNotCallDetection ? 'opacity-100' : 'opacity-0'}
                          transition-opacity duration-200 ease-in-out
                        `}
                          >
                            <svg
                              className="h-3 w-3 text-neutral-0"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <path
                                d="M3 6l2 2 4-4"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </Checkbox>

                        <span className="text-sm text-neutral-600">
                          Do not call detection
                        </span>
                      </div>

                      <div className="mb-3 md:mb-0 w-full md:w-1/2 flex items-center gap-2">
                        <Checkbox
                          checked={hipaaCompliant}
                          onChange={(newChecked) =>
                            setHipaaCompliant(newChecked)
                          }
                          className={`
                        ${hipaaCompliant ? 'bg-primary border-transparent' : 'bg-neutral-0 border-neutral-200'}
                        relative inline-flex h-5 w-5 items-center justify-center rounded border-2
                        transition-colors duration-200 ease-in-out
                        focus:outline-none
                      `}
                        >
                          <span
                            className={`
                          pointer-events-none absolute h-3 w-3 transform
                          ${hipaaCompliant ? 'opacity-100' : 'opacity-0'}
                          transition-opacity duration-200 ease-in-out
                        `}
                          >
                            <svg
                              className="h-3 w-3 text-neutral-0"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <path
                                d="M3 6l2 2 4-4"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </Checkbox>

                        <span className="text-sm text-neutral-600">
                          HIPAA Compliant
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              ),
            },
            ...(!disableCallContext && Object.keys(callContext).length > 0
              ? [
                  {
                    label: 'Call Context',
                    content: (
                      <CallContextForm
                        contextObject={callContext}
                        setContextObject={setCallContext}
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Modal>
    </>
  );
};
